// MWA-257 — Company-name normalization (frontend equivalent).
//
// Mirrors backend/layers/helpers/nodejs/companyName.mjs. Used by the Target
// Companies tab to power a non-blocking "Did you mean {X}?" hint while a user
// types a company name in the Add dialog. Keep the two in sync.

// Trailing legal / structural suffixes that don't change the underlying brand.
const LEGAL_SUFFIXES = [
  'incorporated',
  'inc',
  'llc',
  'ltd',
  'limited',
  'corporation',
  'corp',
  'company',
  'co',
  'plc',
  'gmbh',
  'sa',
  'ag',
  'holdings',
  'group',
]

const SUFFIX_SET = new Set(LEGAL_SUFFIXES)

/**
 * Normalize a company name into a comparison key.
 *
 * Lowercase → strip punctuation → strip a leading "the" → strip trailing legal
 * suffixes (inc, llc, ltd, corp, co, holdings, group, …) → collapse whitespace.
 */
export function normalizeCompanyName(name: string | undefined | null): string {
  if (!name || typeof name !== 'string') return ''

  let key = name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!key) return ''

  let tokens = key.split(' ')

  if (tokens.length > 1 && tokens[0] === 'the') {
    tokens = tokens.slice(1)
  }

  while (tokens.length > 1 && SUFFIX_SET.has(tokens[tokens.length - 1])) {
    tokens = tokens.slice(0, -1)
  }

  return tokens.join(' ')
}

/**
 * The key that decides whether two contacts work at the same company, for
 * grouping and filtering. Falls back to the lowercased name when
 * normalization strips everything (e.g. a non-Latin name), so such names
 * stay distinct instead of all collapsing into one empty key.
 */
export function companyGroupKey(name: string): string {
  return normalizeCompanyName(name) || name.trim().toLowerCase()
}

/**
 * One display label per company group key: the spelling most names use, with
 * ties going to the capitalised spelling ("razor" + "Razor" reads "Razor").
 */
export function companyGroupLabels(names: string[]): Map<string, string> {
  const spellingsByKey = new Map<string, Map<string, number>>()
  for (const raw of names) {
    const name = raw.trim()
    const key = companyGroupKey(name)
    const spellings = spellingsByKey.get(key) ?? new Map<string, number>()
    spellings.set(name, (spellings.get(name) ?? 0) + 1)
    spellingsByKey.set(key, spellings)
  }

  const labels = new Map<string, string>()
  for (const [key, spellings] of spellingsByKey) {
    const [[label]] = Array.from(spellings).sort(
      ([a, countA], [b, countB]) =>
        countB - countA || a.localeCompare(b, 'en', { caseFirst: 'upper' })
    )
    labels.set(key, label)
  }
  return labels
}

/**
 * Lightweight similarity score (0..1) between two strings, based on
 * Levenshtein edit distance over their normalized keys. Used as a fallback to
 * catch typos that don't normalize to an exact match (e.g. "Gogole" vs
 * "Google"). Returns 1 for an exact normalized match.
 */
export function companyNameSimilarity(a: string, b: string): number {
  const ka = normalizeCompanyName(a)
  const kb = normalizeCompanyName(b)
  if (!ka || !kb) return 0
  if (ka === kb) return 1

  const distance = levenshtein(ka, kb)
  const maxLen = Math.max(ka.length, kb.length)
  if (maxLen === 0) return 1
  return 1 - distance / maxLen
}

function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m

  let prev = new Array<number>(n + 1)
  let curr = new Array<number>(n + 1)
  for (let j = 0; j <= n; j++) prev[j] = j

  for (let i = 1; i <= m; i++) {
    curr[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost)
    }
    ;[prev, curr] = [curr, prev]
  }
  return prev[n]
}

/**
 * Given a typed name and a list of existing company names, return the best
 * fuzzy match (exact normalized-key equality, OR similarity above a threshold)
 * — or null if nothing is close enough. Used for the non-blocking "did you
 * mean?" hint, so it never blocks submission.
 */
export function findSimilarCompany(
  typed: string,
  existing: string[],
  threshold = 0.82
): string | null {
  const typedKey = normalizeCompanyName(typed)
  if (!typedKey) return null

  let best: { name: string; score: number } | null = null
  for (const name of existing) {
    const key = normalizeCompanyName(name)
    if (!key) continue
    // Don't suggest the exact same string the user already typed.
    if (key === typedKey && name.trim().toLowerCase() === typed.trim().toLowerCase()) {
      continue
    }
    const score = key === typedKey ? 1 : companyNameSimilarity(typed, name)
    if (score >= threshold && (!best || score > best.score)) {
      best = { name, score }
    }
  }

  return best ? best.name : null
}
